import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Calendar, Folder, ExternalLink, Loader2, Sparkles, FileDown } from "lucide-react";
import { Link } from "react-router-dom";
import {
  loadRecents,
  saveRecents,
  markRecentStorage,
  fetchIndex,
  fileLabelFromPath,
} from "../lib/solutions.js";

function getParam(name) {
  try {
    return new URLSearchParams(window.location.search).get(name) || "";
  } catch {
    return "";
  }
}

function deriveRepoSlug(repoKey) {
  const parts = String(repoKey || "").split("/");
  return parts[2] || "";
}

function fmtDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
}

function buildProjectHtmlUrl(repoSlug, docSlug) {
  if (!repoSlug || !docSlug) return "";
  return `/projectv2/${encodeURIComponent(repoSlug)}/${encodeURIComponent(docSlug)}/index.html`;
}

function extractContentHtml(rawHtml, basePath) {
  const parsed = new DOMParser().parseFromString(rawHtml, "text/html");

  let root = parsed.querySelector("main") || parsed.querySelector("article") || parsed.body;

  root.querySelectorAll("script, style, link, meta, noscript").forEach((n) => n.remove());

  const unwrapSelectors = [
    ".markdown-body",
    ".content",
    ".content-wrapper",
    ".page-content",
    ".container",
    ".wrapper",
    ".prose",
  ];

  for (const sel of unwrapSelectors) {
    const el = root.querySelector(sel);
    if (el && el.innerHTML && el.innerHTML.length > 20) {
      root = el;
      break;
    }
  }

  const fixUrl = (u) => {
    const s = String(u || "").trim();
    if (!s) return s;
    if (s.startsWith("#")) return s;
    if (/^(https?:)?\/\//i.test(s)) return s;
    if (/^(mailto:|tel:)/i.test(s)) return s;
    if (s.startsWith("/")) return s;
    return basePath.replace(/\/+$/, "") + "/" + s.replace(/^\.?\//, "");
  };

  root.querySelectorAll("a[href]").forEach((a) => {
    a.setAttribute("href", fixUrl(a.getAttribute("href")));
    a.setAttribute("target", "_blank");
    a.setAttribute("rel", "noreferrer");
  });

  root.querySelectorAll("img[src]").forEach((img) => {
    img.setAttribute("src", fixUrl(img.getAttribute("src")));
    img.setAttribute("loading", "lazy");
  });

  return root.innerHTML;
}

function humanizeTitle(s) {
  return String(s || "")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function getStaticRepoFiles(repoSlug) {
  const slug = String(repoSlug || "").toLowerCase();

  if (slug === "epos") {
    const slugs = ["epos-development-phase1-2", "epos-development-phase3", "ocr"];
    return slugs.map((docSlug) => ({
      slug: docSlug,
      title: humanizeTitle(docSlug),
      path: `${docSlug}.md`,
      updatedAt: null,
      sourceUrl: "",
      repoSlug: "epos",
    }));
  }

  return [];
}

export default function SolutionsAndInsightsViewer() {
  const repoKey = decodeURIComponent(getParam("repoKey") || getParam("repo") || "");
  const title = decodeURIComponent(getParam("title") || "");
  const videoEmbed = decodeURIComponent(getParam("videoEmbed") || getParam("video") || "");

  useEffect(() => {
    markRecentStorage(repoKey);
  }, [repoKey]);

  const [indexItems, setIndexItems] = useState([]);
  const [activeSlug, setActiveSlug] = useState("");

  const [docHtml, setDocHtml] = useState("");
  const [docErr, setDocErr] = useState("");
  const [docLoading, setDocLoading] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      const items = await fetchIndex();
      if (alive) setIndexItems(items);
    })();
    return () => {
      alive = false;
    };
  }, []);

  const files = useMemo(() => {
    if (!repoKey) return [];

    const arr = indexItems
      .filter((it) => (it.repo || "") === repoKey)
      .map((it) => ({
        slug: it.slug,
        title: it.title,
        path: it.path,
        updatedAt: it.updatedAt,
        sourceUrl: it.sourceUrl,
        repoSlug: it.repoSlug,
      }))
      .filter((f) => f.slug);

    arr.sort((a, b) => String(a.path || "").localeCompare(String(b.path || "")));

    if (arr.length) return arr;

    const repoSlugFromQuery = decodeURIComponent(getParam("repoSlug") || "");
    const repoSlug = repoSlugFromQuery || deriveRepoSlug(repoKey);
    return getStaticRepoFiles(repoSlug);
  }, [indexItems, repoKey]);

  const lastUpdated = useMemo(() => {
    let t = 0;
    for (const f of files) {
      const dt = f.updatedAt ? new Date(f.updatedAt).getTime() : 0;
      if (dt > t) t = dt;
    }
    return t ? new Date(t).toISOString() : "";
  }, [files]);

  useEffect(() => {
    if (!files.length) return;

    const qDoc = getParam("doc");
    if (qDoc) {
      const found = files.find((f) => f.slug === qDoc);
      if (found) {
        setActiveSlug(found.slug);
        return;
      }
    }

    if (!activeSlug) setActiveSlug(files[0].slug);
  }, [files, activeSlug]);

  const activeFile = useMemo(() => {
    if (!files.length) return null;
    return files.find((f) => f.slug === activeSlug) || files[0] || null;
  }, [files, activeSlug]);

  const [tsdLoading, setTsdLoading] = useState(false);
  const [tsdProgress, setTsdProgress] = useState("");
  const [tsdReady, setTsdReady] = useState(null);

  const TSD_API_BASE = process.env.TSD_API_BASE || "http://localhost:8000";

  const handleDownloadTsd = useCallback(() => {
    if (!tsdReady) return;
    const link = document.createElement("a");
    link.href = tsdReady.url;
    link.download = tsdReady.fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
  }, [tsdReady]);

  const handleGenerateTsd = useCallback(async () => {
    if (tsdLoading || !files.length) return;

    setTsdLoading(true);
    setTsdReady(null);
    setTsdProgress("Collecting project documentation…");

    try {
      const repoSlugFromQuery = decodeURIComponent(getParam("repoSlug") || "");

      const collected = [];
      for (const f of files) {
        const rs = repoSlugFromQuery || f.repoSlug || deriveRepoSlug(repoKey);
        const url = buildProjectHtmlUrl(rs, f.slug);
        if (!url) continue;

        try {
          const res = await fetch(url, { cache: "no-store" });
          if (!res.ok) continue;
          const raw = await res.text();

          const parsed = new DOMParser().parseFromString(raw, "text/html");
          const textContent = (parsed.body && parsed.body.textContent) || "";
          if (textContent.trim()) {
            collected.push({ path: f.path || f.slug, content: textContent.trim() });
          }
        } catch {}
      }

      if (!collected.length) {
        alert("Could not fetch any project documentation content.");
        return;
      }

      setTsdProgress("Generating document...");

      const apiRes = await fetch(`${TSD_API_BASE}/api/generate-tsd`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ files: collected }),
      });

      if (!apiRes.ok) {
        const err = await apiRes.json().catch(() => ({}));
        throw new Error(err.error || `Server responded with ${apiRes.status}`);
      }

      const blob = await apiRes.blob();
      const disposition = apiRes.headers.get("content-disposition") || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const fileName = match ? match[1] : "Generated_TSD.docx";

      const blobUrl = URL.createObjectURL(blob);
      setTsdReady({ url: blobUrl, fileName });
    } catch (e) {
      alert(`TSD generation failed: ${e.message || e}`);
    } finally {
      setTsdLoading(false);
      setTsdProgress("");
    }
  }, [files, repoKey, tsdLoading, TSD_API_BASE]);

  useEffect(() => {
    let alive = true;

    async function loadDoc() {
      setDocErr("");
      setDocHtml("");

      if (!activeFile?.slug) return;

      const repoSlugFromQuery = decodeURIComponent(getParam("repoSlug") || "");
      const repoSlug = repoSlugFromQuery || activeFile.repoSlug || deriveRepoSlug(repoKey);
      const docSlug = activeFile.slug;

      const url = buildProjectHtmlUrl(repoSlug, docSlug);
      if (!url) return;

      setDocLoading(true);

      try {
        const res = await fetch(url, { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const raw = await res.text();
        const basePath = `/projectv2/${repoSlug}/${docSlug}/`;
        const html = extractContentHtml(raw, basePath);

        if (!alive) return;
        setDocHtml(html);
      } catch (e) {
        if (!alive) return;
        setDocErr(String(e?.message || e));
      } finally {
        if (alive) setDocLoading(false);
      }
    }

    loadDoc();

    return () => {
      alive = false;
    };
  }, [activeFile?.slug, repoKey]);

  if (!repoKey) {
    return (
      <section className="si si-view">
        <div className="si-emptyState">Missing repo parameter.</div>
      </section>
    );
  }

  return (
    <section className="si si-view">
      <div className="si-viewTop">
        <Link className="si-viewBack" to="/solutions-and-insights">
          ←
        </Link>

        <div className="si-viewHeading">
          <div className="si-viewTitle">{title || repoKey}</div>

          <div className="si-viewSub">
            {lastUpdated ? (
              <span className="si-metaItem">
                <Calendar className="si-metaIcon" aria-hidden="true" />
                <span>Last updated: {fmtDate(lastUpdated)}</span>
              </span>
            ) : null}

            <span className="si-metaItem">
              <Folder className="si-metaIcon" aria-hidden="true" />
              <span>{files.length} files</span>
            </span>

            {activeFile?.sourceUrl ? (
              <span className="si-metaItem">
                <ExternalLink className="si-metaIcon" aria-hidden="true" />
                <a
                  className="si-viewLink"
                  href={activeFile.sourceUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Azure DevOps
                </a>
              </span>
            ) : null}
          </div>
        </div>

        <div className="si-viewActions">
          {videoEmbed ? (
            <a className="si-btn is-outline" href={videoEmbed} target="_blank" rel="noreferrer">
              Watch Video
            </a>
          ) : null}

          {tsdReady ? (
            <button
              type="button"
              className="si-btn si-btn--download"
              onClick={handleDownloadTsd}
              title="Download your generated Technical Specification Document"
            >
              <FileDown className="si-metaIcon" aria-hidden="true" />
              Download Technical Document
            </button>
          ) : (
            <button
              type="button"
              className="si-btn si-btn--generate"
              onClick={handleGenerateTsd}
              disabled={tsdLoading || !files.length}
              title={
                tsdLoading
                  ? tsdProgress
                  : "Generate a Technical Specification Document from this project's documentation"
              }
            >
              {tsdLoading ? (
                <>
                  <Loader2 className="si-metaIcon si-spin" aria-hidden="true" />
                  {tsdProgress || "Generating…"}
                </>
              ) : (
                <>
                  <Sparkles className="si-metaIcon" aria-hidden="true" />
                  Generate Technical Document
                </>
              )}
            </button>
          )}
        </div>
      </div>

      <div className="si-tabs">
        {files.map((f) => (
          <button
            key={f.slug}
            type="button"
            className={`si-tab ${activeFile?.slug === f.slug ? "is-active" : ""}`}
            onClick={() => setActiveSlug(f.slug)}
          >
            {fileLabelFromPath(f.path)}
          </button>
        ))}
      </div>

      <div className="si-docFrame">
        {!activeFile ? (
          <div className="si-emptyState">No markdown files found for this repo.</div>
        ) : docLoading ? (
          <div className="si-emptyState">Loading…</div>
        ) : docErr ? (
          <div className="si-emptyState">Failed to load document: {docErr}</div>
        ) : docHtml ? (
          <div className="si-docHtml" dangerouslySetInnerHTML={{ __html: docHtml }} />
        ) : (
          <div className="si-emptyState">No content.</div>
        )}
      </div>
    </section>
  );
}
